import { cn } from '@/shared/lib/cn';

/**
 * As variantes do botao, num arquivo proprio para que o link com aparencia de botao
 * use as mesmas classes sem importar o componente.
 */
/**
 * A hierarquia, da mais forte para a mais leve: `primary` (a acao da tela), `soft`
 * (a acao principal de um grupo, com o fundo claro da marca), `secondary`, `ghost`
 * (navegacao e acao de apoio) e `danger` (o que encerra ou apaga).
 */
export type ButtonVariant = 'primary' | 'soft' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';

const base =
  'inline-flex items-center justify-center gap-2 font-medium ' +
  'transition-[background-color,border-color,box-shadow,color,transform] ' +
  'duration-(--duration-fast) ease-(--ease-out-soft) ' +
  'active:translate-y-px disabled:pointer-events-none disabled:opacity-50 ' +
  'focus-visible:outline-2 focus-visible:outline-offset-2 ' +
  'focus-visible:outline-brand-500 select-none';

const variants: Record<ButtonVariant, string> = {
  // O brilho de cima e um degrade sobre a cor da empresa, e nao outra cor: o botao
  // continua com a cor que a empresa escolheu.
  primary:
    'bg-brand-500 bg-linear-to-b from-white/12 to-transparent text-white ' +
    'shadow-(--shadow-brand) hover:bg-brand-600 active:bg-brand-700',
  soft:
    'bg-brand-50 text-brand-700 border border-brand-200 shadow-(--shadow-xs) ' +
    'hover:bg-brand-100 hover:border-brand-300',
  secondary:
    'bg-surface text-ink-700 border border-line-strong shadow-(--shadow-xs) ' +
    'hover:border-brand-300 hover:bg-brand-50 hover:text-brand-700',
  ghost: 'bg-transparent text-ink-600 hover:bg-brand-50 hover:text-brand-700',
  danger:
    'bg-surface text-danger-700 border border-danger-200 shadow-(--shadow-xs) ' +
    'hover:bg-danger-50 hover:border-danger-500/50',
};

const sizes: Record<ButtonSize, string> = {
  sm: 'h-8 rounded-md px-3 text-xs',
  md: 'h-10 rounded-control px-4 text-sm',
  lg: 'h-12 rounded-xl px-6 text-base',
};

/**
 * As classes do botao, para o link que tem a aparencia de botao (como "Relatorios" no
 * painel). Assim o link e o botao ficam iguais sem copiar a lista de classes.
 */
export function buttonClassName({
  variant = 'primary',
  size = 'md',
  className,
}: {
  variant?: ButtonVariant | undefined;
  size?: ButtonSize | undefined;
  className?: string | undefined;
} = {}): string {
  return cn(base, variants[variant], sizes[size], className);
}
